import React from "react";

import type { AxiosRequestConfig } from "axios";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";

import {
  IconCalendarFill,
  IconFeedbackFill,
  IconFile,
  IconFileAvi,
  IconFileDoc,
  IconFileDocx,
  IconFileGeoTiff,
  IconFileIfc,
  IconFileLas,
  IconFileLaz,
  IconFileMp4,
  IconFilePdf,
  IconFileShp,
  IconFileTif,
  IconFileTiff,
  IconFileTxt,
  IconFileUndefined,
  IconFileXls,
  IconFileZip,
  IconPinFill,
  IconTools,
} from "@synthesis/shared-icons";
import { downloadDocumentById, type FileMetaDto } from "../../../api";
import { Checkbox, ContextMenu, Tooltip, type ContextMenuItem } from "@synthesis/shared-ui";
import { convertBlobToBase64, cn, logger } from "@synthesis/shared-utils";

import { type Checked, useStores } from "../../../store";
import { DocumentsTypes } from "../../../types";
import { useMarkUp } from "../hooks";

const Wrapper = ({ children, ...rest }: React.HTMLAttributes<HTMLElement>) => {
  return (
    <section className="mb-16 grid grid-cols-[repeat(auto-fill,250px)] gap-4" {...rest}>
      {children}
    </section>
  );
};

const IMAGE_VARIANTS = { png: "png", jpg: "jpg", jpeg: "jpeg", bmp: "bmp", svg: "svg", webp: "webp" } as const;

const isImageExt = (ext: string | null | undefined): ext is keyof typeof IMAGE_VARIANTS => !!ext && ext.toLowerCase() in IMAGE_VARIANTS;

const formatFileSize = (bytes: number) => {
  if (!bytes) return "0 Б";
  const kb = 1024;
  const mb = kb * 1024;
  const gb = mb * 1024;
  if (bytes < mb) return `${(bytes / kb).toFixed(1)} Кб`;
  if (bytes < gb) return `${(bytes / mb).toFixed(1)} Мб`;
  return `${(bytes / gb).toFixed(2)} Гб`;
};

type FileProps = {
  data: FileMetaDto & { progress?: number };
  onClickModel: (data: FileMetaDto) => void;
  onContextMenu: (e: React.MouseEvent, data: FileMetaDto) => void;
  onClickFile: (e: React.MouseEvent, imgSrc: string | null, data: FileMetaDto) => void;
  isEngineering: boolean;
  isModel: boolean;
  isUsingInGantt: boolean;
  setSelectedFile: (data: unknown) => void;
  state: ContextMenuItem[];
  stateFunctions: {
    openViewer: (file?: FileMetaDto) => void;
    openDoc: (file?: FileMetaDto) => void;
  };
};

const File = observer<FileProps>(
  ({ onClickModel, setSelectedFile, data, isModel, isUsingInGantt, isEngineering, onClickFile, state, stateFunctions }) => {
    const { generalStore } = useStores();
    const [imgSrc, setImgSrc] = React.useState<string | null>(null);
    const { isLoading, markupsCount } = useMarkUp(data.id);

    const handleCheck = (checkedData: Checked) => {
      generalStore.setCheckedContents(checkedData);
    };

    const isChecked = React.useMemo(() => generalStore.checkedFiles.get(data.id) !== undefined, [generalStore.checkedFiles, data]);
    const pdfPreview = generalStore.pdfPreviewsByFileId.get(data.id);
    const displaySrc = imgSrc ?? pdfPreview?.previewSrc ?? null;
    const isPdf = data.type === DocumentsTypes.pdf_document || data.ext?.toLowerCase() === "pdf";
    const authorName = generalStore.getAuthorName(data.author);
    const isAuthorLoading = generalStore.activeTab === "docs" && generalStore.isAuthorNamesLoading && !generalStore.authorNamesById.has(data.author);

    React.useEffect(
      function fetchImage() {
        (async () => {
          if (isImageExt(data.ext)) {
            try {
              const config: AxiosRequestConfig = { responseType: "blob" };
              const imageBlob = await downloadDocumentById(data.id, config);
              const base64Src = await convertBlobToBase64(imageBlob);
              setImgSrc(base64Src as string);
            } catch (err) {
              logger.error("downloadImageFile", err);
            }
          }
        })();
      },
      [data, imgSrc],
    );

    React.useEffect(() => {
      if (generalStore.activeTab !== "docs" || !data.author || generalStore.authorNamesById.has(data.author)) {
        return;
      }

      generalStore.fetchAuthorNames([data]);
    }, [data, data.author, generalStore.activeTab, generalStore]);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (generalStore.activeTab === "docs") {
        setSelectedFile(data);
        stateFunctions.openDoc(data);
        return;
      }

      stateFunctions.openViewer(data);
      if (data.type === DocumentsTypes.model) onClickModel(data);
      if (data.type !== DocumentsTypes.model) onClickFile(e, displaySrc, data);
    };

    const icon = React.useMemo(() => {
      const ext = data.ext?.toLowerCase();

      if (ext === "pdf") return <IconFilePdf className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
      if (ext === "doc") return <IconFileDoc className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
      if (ext === "docx") return <IconFileDocx className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
      if (ext === "ifc") return <IconFileIfc data-cy="gridView-iconFileIfc-filesystem" className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
      if (ext === "tiff") return <IconFileTiff className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
      if (ext === "geotiff") return <IconFileGeoTiff className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
      if (ext === "tif") return <IconFileTif className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
      if (ext === "avi") return <IconFileAvi className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
      if (ext === "mp4") return <IconFileMp4 className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
      if (ext === "las") return <IconFileLas className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
      if (ext === "laz") return <IconFileLaz className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
      if (ext === "shp") return <IconFileShp className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
      if (ext === "zip") return <IconFileZip className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
      if (ext === "txt") return <IconFileTxt className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
      if (ext === "xls") return <IconFileXls className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
      if (ext === "xlsx") return <IconFileXls className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
      return <IconFileUndefined className="text-neutrals-90 h-[6.5rem] w-[6.5rem]" />;
    }, [data.ext]);

    const additional = (
      <div className="absolute inset-x-0 -bottom-1 flex justify-center gap-2">
        {(isEngineering || isModel || isUsingInGantt) && (
          <div className="bg-tertiary-90 flex gap-1 rounded-lg p-[0.375rem]">
            {isEngineering && (
              <Tooltip arrowSide="top" arrowAlign="center" description="Инженерное изыскание">
                <figure>
                  <IconPinFill className="text-tertiary-60 size-4" />
                </figure>
              </Tooltip>
            )}
            {isModel && (
              <Tooltip arrowSide="top" arrowAlign="center" description="Основная модель">
                <figure>
                  <IconTools className="text-tertiary-60 size-4" />
                </figure>
              </Tooltip>
            )}
            {isUsingInGantt && (
              <Tooltip arrowSide="top" arrowAlign="center" description="Участвует в сетевом графике">
                <figure>
                  <IconCalendarFill className="text-tertiary-60 size-4" />
                </figure>
              </Tooltip>
            )}
          </div>
        )}
        {(markupsCount ?? 0) > 0 && (
          <div className="bg-system-error-50 flex gap-1 rounded-lg p-[0.375rem]">
            <Tooltip arrowSide="top" arrowAlign="center" title="Доступных замечаний:" description={markupsCount}>
              <figure>
                <IconFeedbackFill className="text-system-error-90 size-4" />
              </figure>
            </Tooltip>
          </div>
        )}
      </div>
    );

    return (
      <ContextMenu state={state} disabled={!!data.progress} onClick={() => setSelectedFile(data)}>
        <Container data-cy="gridView-container-projectDocks" isChecked={isChecked} onClick={handleClick}>
          <figure className="relative mx-auto">
            {displaySrc ? (
              <div className="relative mx-auto flex h-[6.5rem] w-[6.5rem] shrink-0 items-center justify-center overflow-hidden">
                <img className="max-h-full max-w-full object-contain object-center" src={displaySrc} alt="" />
              </div>
            ) : (
              icon
            )}
            {additional}
          </figure>

          <h2 data-cy="gridView-h2-fileSystem" className="text-type-common paragraph-base mt-2 break-words px-2">
            {data.baseName}
            <span className="text-type-light paragraph-sm">{` (${formatFileSize(data.size)})`}</span>
          </h2>

          {!isLoading && (markupsCount ?? 0) > 0 && <p className="text-system-error-50 paragraph-sm px-2">Замечаний ({markupsCount})</p>}

          <>
            {data.version > 1 && (
              <span data-cy="gridView-span-fileVersion" className="bg-tertiary-90 subtitle-sm absolute left-10 top-20 rounded-lg p-2.5">
                Версия {data.version}
              </span>
            )}
            {isPdf && pdfPreview && pdfPreview.pageCount > 0 && (
              <div className="bg-brand-30 subtitle-sm absolute left-1/2 top-20 flex -translate-x-1/2 items-center gap-1 rounded-lg p-2.5 text-white">
                <IconFile className="text-brand-90 size-5" />
                <span>{pdfPreview.pageCount}</span>
              </div>
            )}
          </>
          {!data.progress && (
            <p data-cy="gridView-p-fileSystem" className="text-type-light paragraph-sm px-2">
              {dayjs.unix(Number(data.modifiedAt)).format("DD.MM.YYYY HH:mm:ss")}
            </p>
          )}

          {generalStore.activeTab === "docs" && !data.progress && (
            <p className="text-type-light paragraph-sm truncate px-2" title={authorName}>
              {isAuthorLoading ? <span className="bg-grey-300 inline-block h-4 w-24 animate-pulse rounded" /> : authorName}
            </p>
          )}

          {typeof data.progress === "number" && data.progress > 0 && (
            <div className="relative mb-2 h-1">
              <div className="bg-neutrals-90 absolute inset-0 h-1 w-full rounded-full">
                <div className="bg-neutrals-50 h-1 rounded-full" style={{ width: `${data.progress}%` }}></div>
                <div data-cy="gridView-div-fileSystem" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform text-black">
                  {data.progress.toFixed(0)}%
                </div>
              </div>
            </div>
          )}

          <CheckboxWrapper isChecked={isChecked} onClick={(e) => e.stopPropagation()}>
            <Checkbox data-cy="gridView-checkbox-projectDocks" onChange={() => handleCheck(data)} checked={isChecked} />
          </CheckboxWrapper>
        </Container>
      </ContextMenu>
    );
  },
);

const CheckboxWrapper = ({ isChecked, className, ...props }: React.HTMLAttributes<HTMLDivElement> & { isChecked?: boolean }) => (
  <div className={cn("absolute left-2 z-30 opacity-0 group-hover:opacity-100 sm:top-2", isChecked && "opacity-100", className)} {...props} />
);

type ContainerProps = React.HTMLAttributes<HTMLDivElement> & { isChecked: boolean };

const Container = ({ isChecked, className, ...props }: ContainerProps) => (
  <div
    className={cn(
      "hover:bg-neutrals-99 group relative flex w-[10.25rem] cursor-pointer flex-col gap-1 rounded-[14px] pb-12 transition-colors",
      isChecked && "bg-tertiary-95 hover:bg-tertiary-95/75",
      className,
    )}
    {...props}
  />
);

const GridView = {
  File,
  Wrapper,
};

export default GridView;
